import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAppContext } from "../App";
import { useShopify } from "../providers/ShopifyAppBridgeProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  X,
  Plus,
  Minus,
  Trash2,
  Loader2,
  Check,
  Download,
  Maximize2,
  CheckSquare,
  XSquare,
  FileX,
  Info,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

/**
 * ShopifyOrderWaybillPage
 * 
 * This page is designed to be opened from Shopify Admin's "More actions" dropdown.
 * It receives the order_id from URL parameters and fetches the order from Shopify API.
 */
const ShopifyOrderWaybillPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { shop, orderId, isEmbedded, isAuthenticated, isLoading: authLoading, initiateOAuth } = useShopify();
  const { packages, fetchPackages } = useAppContext();
  
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdWaybill, setCreatedWaybill] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [selectedService, setSelectedService] = useState("ECO");
  const [serviceLocked, setServiceLocked] = useState(false);
  const [showSANotice, setShowSANotice] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    customer: "",
    account: "",
    date: new Date().toISOString().split('T')[0],
    costing: "N/A",
    from_company: "",
    from_place: "",
    from_address1: "",
    from_address2: "",
    from_address3: "",
    from_address4: "",
    from_contact: "",
    from_telephone1: "",
    from_telephone2: "",
    from_cell: "",
    from_email: "",
    from_notify: false,
    to_company: "",
    to_place: "",
    to_address1: "",
    to_address2: "",
    to_address3: "",
    to_address4: "",
    to_contact: "",
    to_telephone1: "",
    to_telephone2: "",
    to_cell: "",
    to_email: "",
    to_notify: false,
    reference: "",
    additional_services: "N/A",
    insurance: false,
    declared_value: "",
    contents: []
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (!authLoading) {
      fetchData();
    }
  }, [orderId, shop, isAuthenticated, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Always fetch settings
      const settingsRes = await axios.get(`${API}/settings`);
      const settingsData = settingsRes.data;
      setSettings(settingsData);

      // Wire Settings "Default Service": start on the chosen service. A non-ECO default is a
      // deliberate choice, so lock it (route-based auto LOC/ECO won't override it below).
      const defService = (settingsData?.default_service || "ECO").toUpperCase();
      setSelectedService(defService);
      setServiceLocked(defService !== "ECO");

      let orderData = null;

      // If we have shop and orderId, fetch from Shopify API
      if (shop && orderId && isAuthenticated) {
        try {
          const shopifyOrderRes = await axios.get(`${API}/shopify/order/${orderId}?shop=${shop}`);
          orderData = shopifyOrderRes.data;
        } catch (shopifyError) {
          console.error("Failed to fetch Shopify order:", shopifyError);
          // If Shopify fetch fails, we'll show an error
          if (shopifyError.response?.status === 401) {
            setError("Shop not authenticated. Please reinstall the app.");
            setLoading(false);
            return;
          }
          throw shopifyError;
        }
      } else if (orderId && !shop) {
        // Fallback to local order if no shop (standalone mode)
        try {
          const localOrderRes = await axios.get(`${API}/orders/${orderId}`);
          orderData = localOrderRes.data;
        } catch (localError) {
          console.error("Failed to fetch local order:", localError);
        }
      }

      if (!orderData) {
        setError("No order found. Please access this page from a Shopify order.");
        setLoading(false);
        return;
      }

      setOrder(orderData);
      populateForm(orderData, settingsData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.response?.data?.detail || "Failed to load order data");
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (orderData, settingsData) => {
    const shipping = orderData.shipping_address || {};
    
    // Service (LOC vs ECO) is set from the live First Freight quote in handleSubmit —
    // FF only returns a LOC rate for genuinely local routes.
    
    setFormData(prev => ({
      ...prev,
      customer: settingsData.company_name || "",
      account: settingsData.ff_account || "",
      from_company: settingsData.company_name || "",
      from_place: settingsData.warehouse_name || "",
      from_address1: settingsData.address_line1 || "",
      from_address2: settingsData.address_line2 || "",
      from_address3: settingsData.city || "",
      from_address4: "",
      from_contact: settingsData.contact_person || "",
      from_telephone1: settingsData.phone || "",
      from_telephone2: "",
      from_cell: "",
      from_email: settingsData.email || "",
      to_company: orderData.customer_name,
      to_place: shipping.city || "",
      to_address1: shipping.address1 || "",
      to_address2: shipping.address2 || "",
      to_address3: shipping.city || "",
      to_address4: shipping.province || "",
      to_contact: orderData.customer_name,
      to_telephone1: orderData.customer_phone || "",
      to_telephone2: "",
      to_cell: orderData.customer_phone || "",
      to_email: orderData.customer_email || "",
      reference: `FF-${orderData.order_number}`,
      contents: (orderData.line_items || []).map((item, index) => ({
        id: index + 1,
        pieces: item.quantity || 1,
        description: `${item.name}${item.variant ? ` - ${item.variant}` : ''} (${item.sku || 'N/A'})`,
        length: 50,
        width: 50,
        height: 50,
        mass: item.weight || 1
      }))
    }));
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateContent = (index, field, value) => {
    setFormData(prev => {
      const contents = [...prev.contents];
      contents[index] = { ...contents[index], [field]: value };
      return { ...prev, contents };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      contents: [...prev.contents, {
        id: prev.contents.length + 1,
        pieces: 1,
        description: "",
        length: "",
        width: "",
        height: "",
        mass: 1
      }]
    }));
  };

  const addFromPackagePreset = (pkg) => {
    setFormData(prev => ({
      ...prev,
      contents: [...prev.contents, {
        id: prev.contents.length + 1,
        pieces: 1,
        description: pkg.name,
        length: pkg.length,
        width: pkg.width,
        height: pkg.height,
        mass: pkg.weight
      }]
    }));
    toast.success(`Added ${pkg.name}`);
  };

  const removeItem = () => {
    if (formData.contents.length === 0) return;
    setFormData(prev => ({
      ...prev,
      contents: prev.contents.slice(0, -1)
    }));
  };

  const clearAllItems = () => {
    setFormData(prev => ({ ...prev, contents: [] }));
  };

  const handleSubmit = async () => {
    if (!formData.from_company || !formData.to_company) {
      toast.error("Please fill in sender and recipient details");
      return;
    }

    if (formData.contents.length === 0) {
      toast.error("Please add at least one content item");
      return;
    }

    setSubmitting(true);
    try {
      // Call First Freight API for real pricing
      const quotePayload = {
        origpers: formData.from_company,
        origpercontact: formData.from_contact || formData.from_company,
        origperadd1: formData.from_address1 || "Address",
        origperphone: formData.from_telephone1 || formData.from_cell || "",
        origpercell: formData.from_cell || formData.from_telephone1 || "",
        origplace: formData.from_place_id || settings?.place_id || null,
        origtown: formData.from_place || "",
        origperpcode: formData.from_postal_code || settings?.postal_code || "",
        destpers: formData.to_company,
        destpercontact: formData.to_contact || formData.to_company,
        destperadd1: formData.to_address1 || "Address",
        destperphone: formData.to_telephone1 || formData.to_cell || "",
        destpercell: formData.to_cell || formData.to_telephone1 || "",
        destplace: formData.to_place_id || null,
        desttown: formData.to_place || "",
        destperpcode: formData.to_postal_code || "",
        service: selectedService,
        contents: formData.contents.map((c, i) => ({
          item: i + 1,
          pieces: parseInt(c.pieces) || 1,
          description: c.description || "Package",
          dim1: parseFloat(c.length) || 10,
          dim2: parseFloat(c.width) || 10,
          dim3: parseFloat(c.height) || 10,
          actmass: parseFloat(c.mass) || 1
        }))
      };

      const quoteResponse = await axios.post(`${API}/ff/get-quote`, quotePayload, { timeout: 20000 });
      if (quoteResponse.data && quoteResponse.data.length > 0) {
        const q = quoteResponse.data[0];
        setQuoteResult(q);
        // Route-based service: FF only returns a LOC rate for local routes; otherwise ECO.
        // Skip when the merchant locked a specific default service in Settings.
        const hasLoc = (q.rates || []).some(r => (r.service || "").toUpperCase() === "LOC");
        const routeService = hasLoc ? "LOC" : "ECO";
        if (!serviceLocked && routeService !== selectedService) {
          setSelectedService(routeService);
          toast.info(hasLoc
            ? "Local delivery — using Local (LOC) service"
            : "Long-distance delivery — using Economy (ECO) service");
        }
        toast.success("Quote received! Review pricing below.");
      } else {
        toast.warning("No rates returned. You can still create the waybill.");
        setQuoteResult({ rates: [] });
      }
    } catch (error) {
      console.warn("Quote API failed:", error);
      toast.info("Could not fetch live pricing. You can still proceed.");
      setQuoteResult({ rates: [] });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmWaybill = async () => {
    setSubmitting(true);
    try {
      const totalWeight = formData.contents.reduce(
        (sum, c) => sum + (parseFloat(c.mass) || 0) * (parseInt(c.pieces) || 1), 
        0
      );
      
      const internalOrderId = order.id || `shopify-${orderId}`;
      
      const payload = {
        order_id: internalOrderId,
        sender_company: formData.from_company,
        sender_address1: formData.from_address1,
        sender_address2: formData.from_address2,
        sender_address3: formData.from_address3,
        sender_address4: formData.from_address4,
        sender_postal_code: formData.from_postal_code || settings?.postal_code || "",
        sender_city: formData.from_place,
        sender_phone: formData.from_telephone1,
        sender_contact: formData.from_contact,
        recipient_name: formData.to_company,
        recipient_address1: formData.to_address1,
        recipient_address2: formData.to_address2,
        recipient_address3: formData.to_address3,
        recipient_address4: formData.to_address4,
        recipient_postal_code: formData.to_postal_code || "",
        recipient_city: formData.to_place,
        recipient_phone: formData.to_telephone1,
        recipient_email: formData.to_email,
        service_type: selectedService,
        special_instructions: "",
        reference: formData.reference,
        num_parcels: formData.contents.reduce((sum, c) => sum + (parseInt(c.pieces) || 1), 0),
        total_weight: totalWeight,
        contents: formData.contents.map((c, i) => ({
          item: i + 1,
          description: c.description,
          pieces: parseInt(c.pieces) || 1,
          dim1: parseFloat(c.length) || 1,
          dim2: parseFloat(c.width) || 1,
          dim3: parseFloat(c.height) || 1,
          actmass: parseFloat(c.mass) || 1
        }))
      };

      const response = await axios.post(`${API}/waybills`, payload);
      setCreatedWaybill(response.data);

      if (shop && isAuthenticated && orderId) {
        try {
          await axios.post(
            `${API}/shopify/order/${orderId}/metafield?shop=${shop}`,
            null,
            { params: { waybill_number: response.data.waybill_number } }
          );
        } catch (metafieldError) {
          console.warn("Failed to update Shopify metafield:", metafieldError);
        }
      }

      toast.success("Waybill created successfully!");
    } catch (error) {
      console.error("Error creating waybill:", error);
      toast.error(error.response?.data?.detail || "Failed to create waybill");
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    // If embedded, try to close or go back
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/");
    }
  };

  const downloadPDF = async () => {
    if (!createdWaybill) return;
    try {
      const response = await axios.get(`${API}/waybills/${createdWaybill.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waybill-${createdWaybill.waybill_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F0F0F0]" data-testid="loading-state">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#16a34a] mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading order data...</p>
        </div>
      </div>
    );
  }

  // Authentication required state
  if (shop && !isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F0F0F0]" data-testid="auth-required-state">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">
            This app needs to be authorized to access your Shopify store.
          </p>
          <Button onClick={initiateOAuth} className="bg-[#5C6AC4] hover:bg-[#4959BD]">
            Authorize App
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F0F0F0]" data-testid="error-state">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Error Loading Order</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex justify-center gap-2">
            <Button onClick={fetchData} variant="outline">
              Retry
            </Button>
            <Button onClick={handleExit} variant="outline">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No order state
  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F0F0F0]" data-testid="no-order-state">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md text-center">
          <Info className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">No Order Selected</h2>
          <p className="text-gray-600 mb-4">
            Please access this page from a Shopify order's "More actions" menu.
          </p>
          <Button onClick={handleExit} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (createdWaybill) {
    return (
      <div className="min-h-screen bg-[#F0F0F0] p-4" data-testid="success-state">
        <div className="max-w-lg mx-auto mt-20 bg-white border border-gray-300 shadow-lg">
          <div className="bg-[#E8E8E8] px-4 py-2 border-b border-gray-300 flex items-center justify-between">
            <span className="font-semibold text-gray-700">Quote Submitted</span>
            <button onClick={handleExit} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Quote Created Successfully</h2>
            
            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">MOCK</Badge>
              <p className="text-xs text-amber-700 mt-1">Test quote - no live shipment created</p>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <p className="text-xs text-gray-500 mb-1">Waybill Number</p>
              <p className="text-xl font-mono font-bold text-[#16a34a]" data-testid="waybill-number">
                {createdWaybill.waybill_number}
              </p>
              <p className="text-xs text-gray-500 mt-2">Reference: {createdWaybill.reference}</p>
              <p className="text-xs text-gray-500">Order: #{order.order_number}</p>
            </div>

            <div className="flex justify-center gap-2">
              <Button onClick={downloadPDF} size="sm" className="bg-[#4A7C4E] hover:bg-[#3D6840] text-white" data-testid="download-pdf-btn">
                <Download className="w-4 h-4 mr-1" />
                Download PDF
              </Button>
              <Button onClick={handleExit} variant="outline" size="sm" data-testid="exit-btn">
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-[#F0F0F0]" data-testid="waybill-form">
      {/* Header */}
      <div className="bg-[#E8E8E8] border-b border-gray-300 px-4 py-2">
        <h1 className="text-sm font-semibold text-gray-700">EasyWaybill</h1>
      </div>

      {/* Tab Navigation */}
      <div className="bg-[#F5F5F5] border-b border-gray-300 px-4">
        <div className="flex items-center gap-1">
          <TabButton icon="📋" label="Quotes" active />
          <TabButton icon="📦" label="Collections" />
          <TabButton icon="📄" label="Waybills" onClick={() => navigate("/waybills")} />
          <TabButton icon="🧾" label="Invoices" />
          <TabButton icon="📑" label="PODs" />
          <TabButton icon="⚙️" label="My Account" onClick={() => navigate("/settings")} />
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white border-b border-gray-300 px-4 py-1">
        <div className="flex items-center gap-4">
          <button className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            <span className="text-green-600">📝</span> Request Quotes
          </button>
          <button className="text-sm text-gray-600 hover:text-blue-600 flex items-center gap-1">
            <span>🔍</span> View Quotes
          </button>
        </div>
      </div>

      {/* Order Info Banner */}
      {order && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium text-blue-900">Order #{order.order_number}</span>
              <span className="text-blue-700">{order.customer_name}</span>
              <span className="text-blue-600">{order.customer_email}</span>
            </div>
            {isEmbedded && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                Shopify Order
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="p-4">
        <div className="bg-white border border-gray-300 shadow-sm max-w-5xl mx-auto">
          {/* Form Header */}
          <div className="bg-[#E8E8E8] px-4 py-2 border-b border-gray-300 flex items-center justify-between">
            <span className="font-semibold text-gray-700">Request New Quote</span>
            <button onClick={handleExit} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* MOCK Mode Notice */}
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-amber-600" />
            <span className="text-xs text-amber-700">Mock Mode - No live quotes will be created</span>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs ml-auto">MOCK</Badge>
          </div>

          <div className="p-4">
            {/* SA-only notice modal */}
            {showSANotice && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-2xl max-w-md mx-4 overflow-hidden">
                  <div className="bg-red-600 px-6 py-4">
                    <h3 className="text-white font-bold text-base flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.07 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                      South African Courier Service
                    </h3>
                  </div>
                  <div className="px-6 py-5 space-y-3">
                    <p className="text-sm text-gray-700">
                      EasyWaybill integrates with <strong>First Freight</strong>, a South African domestic courier. Suburb verification and shipping quotes work with <strong>South African addresses only</strong>.
                    </p>
                    <p className="text-sm text-gray-700">
                      International postal codes will not return results — this is expected.
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Test with these SA codes:</p>
                      <ul className="text-xs text-gray-600 space-y-0.5">
                        <li><strong>7405</strong> — Maitland, Cape Town</li>
                        <li><strong>2001</strong> — Johannesburg CBD</li>
                        <li><strong>4001</strong> — Durban</li>
                        <li><strong>Greyton</strong> — Western Cape</li>
                      </ul>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end border-t">
                    <button
                      onClick={() => { setShowSANotice(false); navigate(-1); }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid="sa-notice-reject"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={() => setShowSANotice(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                      data-testid="sa-notice-accept"
                    >
                      I Understand, Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Customer & Date Row */}
            <div className="grid grid-cols-2 gap-8 mb-4">
              <div className="space-y-2">
                <FormRow label="Customer:">
                  <Input 
                    value={formData.customer} 
                    onChange={(e) => updateFormData("customer", e.target.value)}
                    className="h-7 text-sm bg-gray-50"
                    disabled
                  />
                </FormRow>
                <FormRow label="Account:">
                  <Input 
                    value={formData.account} 
                    onChange={(e) => updateFormData("account", e.target.value)}
                    className="h-7 text-sm bg-gray-50"
                    disabled
                  />
                </FormRow>
              </div>
              <div className="space-y-2">
                <FormRow label="Date:">
                  <Input 
                    type="date"
                    value={formData.date} 
                    onChange={(e) => updateFormData("date", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Costing:">
                  <Select value={formData.costing} onValueChange={(v) => updateFormData("costing", v)}>
                    <SelectTrigger className="h-7 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="N/A">N/A</SelectItem>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Express">Express</SelectItem>
                    </SelectContent>
                  </Select>
                </FormRow>
              </div>
            </div>

            {/* FROM / TO Section */}
            <div className="grid grid-cols-2 gap-8 border border-gray-200 rounded p-4 mb-4">
              {/* FROM Column */}
              <div className="space-y-2">
                <FormRow label="From:">
                  <Input 
                    value={formData.from_company} 
                    onChange={(e) => updateFormData("from_company", e.target.value)}
                    className="h-7 text-sm"
                    data-testid="from-company"
                  />
                </FormRow>
                <FormRow label="Place:">
                  <Input 
                    value={formData.from_place} 
                    onChange={(e) => updateFormData("from_place", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Address:">
                  <div className="space-y-1">
                    <Input 
                      value={formData.from_address1} 
                      onChange={(e) => updateFormData("from_address1", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 1"
                    />
                    <Input 
                      value={formData.from_address2} 
                      onChange={(e) => updateFormData("from_address2", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 2"
                    />
                    <Input 
                      value={formData.from_address3} 
                      onChange={(e) => updateFormData("from_address3", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="City"
                    />
                    <Input 
                      value={formData.from_address4} 
                      onChange={(e) => updateFormData("from_address4", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Province"
                    />
                  </div>
                </FormRow>
                <FormRow label="Contact:">
                  <Input 
                    value={formData.from_contact} 
                    onChange={(e) => updateFormData("from_contact", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Telephone:">
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      value={formData.from_telephone1} 
                      onChange={(e) => updateFormData("from_telephone1", e.target.value)}
                      className="h-7 text-sm"
                    />
                    <Input 
                      value={formData.from_telephone2} 
                      onChange={(e) => updateFormData("from_telephone2", e.target.value)}
                      className="h-7 text-sm"
                    />
                  </div>
                </FormRow>
                <FormRow label="Cell:">
                  <Input 
                    value={formData.from_cell} 
                    onChange={(e) => updateFormData("from_cell", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Email:">
                  <div className="flex items-center gap-2">
                    <Input 
                      value={formData.from_email} 
                      onChange={(e) => updateFormData("from_email", e.target.value)}
                      className="h-7 text-sm flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <Checkbox 
                        checked={formData.from_notify} 
                        onCheckedChange={(v) => updateFormData("from_notify", v)}
                        className="w-4 h-4"
                      />
                      Notify Contact
                    </label>
                  </div>
                </FormRow>
              </div>

              {/* TO Column */}
              <div className="space-y-2">
                <FormRow label="To:">
                  <Input 
                    value={formData.to_company} 
                    onChange={(e) => updateFormData("to_company", e.target.value)}
                    className="h-7 text-sm"
                    data-testid="to-company"
                  />
                </FormRow>
                <FormRow label="Place:">
                  <Input 
                    value={formData.to_place} 
                    onChange={(e) => updateFormData("to_place", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Address:">
                  <div className="space-y-1">
                    <Input 
                      value={formData.to_address1} 
                      onChange={(e) => updateFormData("to_address1", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 1"
                    />
                    <Input 
                      value={formData.to_address2} 
                      onChange={(e) => updateFormData("to_address2", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Address line 2"
                    />
                    <Input 
                      value={formData.to_address3} 
                      onChange={(e) => updateFormData("to_address3", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="City"
                    />
                    <Input 
                      value={formData.to_address4} 
                      onChange={(e) => updateFormData("to_address4", e.target.value)}
                      className="h-7 text-sm"
                      placeholder="Province"
                    />
                  </div>
                </FormRow>
                <FormRow label="Contact:">
                  <Input 
                    value={formData.to_contact} 
                    onChange={(e) => updateFormData("to_contact", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Telephone:">
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      value={formData.to_telephone1} 
                      onChange={(e) => updateFormData("to_telephone1", e.target.value)}
                      className="h-7 text-sm"
                    />
                    <Input 
                      value={formData.to_telephone2} 
                      onChange={(e) => updateFormData("to_telephone2", e.target.value)}
                      className="h-7 text-sm"
                    />
                  </div>
                </FormRow>
                <FormRow label="Cell:">
                  <Input 
                    value={formData.to_cell} 
                    onChange={(e) => updateFormData("to_cell", e.target.value)}
                    className="h-7 text-sm"
                  />
                </FormRow>
                <FormRow label="Email:">
                  <div className="flex items-center gap-2">
                    <Input 
                      value={formData.to_email} 
                      onChange={(e) => updateFormData("to_email", e.target.value)}
                      className="h-7 text-sm flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                      <Checkbox 
                        checked={formData.to_notify} 
                        onCheckedChange={(v) => updateFormData("to_notify", v)}
                        className="w-4 h-4"
                      />
                      Notify Contact
                    </label>
                  </div>
                </FormRow>
              </div>
            </div>

            {/* Reference & Services Row */}
            <div className="flex items-center gap-4 border border-gray-200 rounded p-3 mb-4">
              <FormRowInline label="Reference:">
                <Input 
                  value={formData.reference} 
                  onChange={(e) => updateFormData("reference", e.target.value)}
                  className="h-7 text-sm w-32"
                  data-testid="reference-input"
                />
              </FormRowInline>
              <FormRowInline label="Additional Services">
                <span className="text-sm text-gray-600">N/A</span>
              </FormRowInline>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <span>Insurance:</span>
                <Checkbox 
                  checked={formData.insurance} 
                  onCheckedChange={(v) => updateFormData("insurance", v)}
                  className="w-4 h-4"
                />
              </label>
              <FormRowInline label="Declared Value :">
                <Input 
                  value={formData.declared_value} 
                  onChange={(e) => updateFormData("declared_value", e.target.value)}
                  className="h-7 text-sm w-24 bg-gray-50"
                  placeholder="N/A"
                  disabled={!formData.insurance}
                />
              </FormRowInline>
            </div>

            {/* Contents Table */}
            <div className="border border-gray-200 rounded mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-16">Pieces</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">Description</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Length (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Width (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-24">Height (cm)</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 w-28">Mass (Total kg)</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.contents.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                        No items added. Click "Add Item" to add contents.
                      </td>
                    </tr>
                  ) : (
                    formData.contents.map((item, index) => (
                      <tr key={item.description ? `${item.description}-${index}` : `content-${index}`} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            min="1"
                            value={item.pieces} 
                            onChange={(e) => updateContent(index, "pieces", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            value={item.description} 
                            onChange={(e) => updateContent(index, "description", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.length} 
                            onChange={(e) => updateContent(index, "length", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.width} 
                            onChange={(e) => updateContent(index, "width", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            value={item.height} 
                            onChange={(e) => updateContent(index, "height", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input 
                            type="number"
                            step="0.1"
                            value={item.mass} 
                            onChange={(e) => updateContent(index, "mass", e.target.value)}
                            className="h-7 text-sm w-full"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Content Actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addItem}
                  className="h-7 text-xs"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-3 h-3 mr-1 text-green-600" />
                  Add Item
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={removeItem}
                  className="h-7 text-xs"
                  disabled={formData.contents.length === 0}
                >
                  <Minus className="w-3 h-3 mr-1 text-red-600" />
                  Remove Item
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearAllItems}
                  className="h-7 text-xs"
                  disabled={formData.contents.length === 0}
                >
                  <Trash2 className="w-3 h-3 mr-1 text-red-600" />
                  Clear All
                </Button>
                
                {packages && packages.length > 0 && (
                  <Select onValueChange={(pkgId) => {
                    const pkg = packages.find(p => p.id === pkgId);
                    if (pkg) addFromPackagePreset(pkg);
                  }}>
                    <SelectTrigger className="h-7 text-xs w-40 ml-2">
                      <SelectValue placeholder="Add from preset..." />
                    </SelectTrigger>
                    <SelectContent>
                      {packages.map(pkg => (
                        <SelectItem key={pkg.id} value={pkg.id} className="text-xs">
                          {pkg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="h-7 text-xs"
              >
                <Maximize2 className="w-3 h-3 mr-1" />
                Expand Contents Grid
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-2 pt-4 border-t border-gray-200">
              <Button 
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#4A7C4E] hover:bg-[#3D6840] text-white h-8 px-4"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <CheckSquare className="w-4 h-4 mr-1" />
                )}
                Submit
              </Button>
              <Button 
                variant="outline"
                onClick={handleExit}
                className="h-8 px-4"
                data-testid="cancel-btn"
              >
                <XSquare className="w-4 h-4 mr-1 text-red-600" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const FormRow = ({ label, children }) => (
  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
    <label className="text-sm text-gray-600 text-right pt-1">{label}</label>
    <div>{children}</div>
  </div>
);

const FormRowInline = ({ label, children }) => (
  <div className="flex items-center gap-2">
    <label className="text-sm text-gray-600">{label}</label>
    {children}
  </div>
);

const TabButton = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`px-3 py-2 text-sm flex items-center gap-1 border-b-2 transition-colors ${
      active 
        ? "border-[#16a34a] text-gray-900 bg-white" 
        : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100"
    }`}
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

export default ShopifyOrderWaybillPage;
